import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import axios from 'axios';
import { Search, MapPin, Package, Thermometer } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const CustomerSearch = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    city: '',
    storageType: 'all',
    temperatureControlled: 'any'
  });
  const [mapCenter, setMapCenter] = useState([41.8781, -87.6298]);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, warehouses]);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API_URL}/warehouses`);
      setWarehouses(response.data);
      setFilteredWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...warehouses];

    if (filters.city) {
      filtered = filtered.filter(w => w.location.city.toLowerCase().includes(filters.city.toLowerCase()));
    }

    if (filters.storageType && filters.storageType !== 'all') {
      filtered = filtered.filter(w => w.storage_types.includes(filters.storageType));
    }

    if (filters.temperatureControlled !== '' && filters.temperatureControlled !== 'any') {
      const isTemp = filters.temperatureControlled === 'true';
      filtered = filtered.filter(w => w.temperature_controlled === isTemp);
    }

    setFilteredWarehouses(filtered);
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        <div className="w-full md:w-96 lg:w-[420px] border-r border-border bg-background overflow-y-auto p-4">
          <div className="mb-6">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-4 tracking-tight">Search Warehouses</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-sm font-medium">City</Label>
                <Input
                  id="city"
                  data-testid="city-filter"
                  placeholder="Enter city name"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="storageType" className="text-sm font-medium">Storage Type</Label>
                <Select value={filters.storageType} onValueChange={(value) => setFilters({ ...filters, storageType: value })}>
                  <SelectTrigger data-testid="storage-type-filter" className="rounded-sm">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="bulk_floor">Bulk Floor</SelectItem>
                    <SelectItem value="racking">Racking</SelectItem>
                    <SelectItem value="variable">Variable (CBM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature" className="text-sm font-medium">Temperature Control</Label>
                <Select value={filters.temperatureControlled} onValueChange={(value) => setFilters({ ...filters, temperatureControlled: value })}>
                  <SelectTrigger data-testid="temperature-filter" className="rounded-sm">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Temperature Controlled</SelectItem>
                    <SelectItem value="false">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => setFilters({ city: '', storageType: 'all', temperatureControlled: 'any' })}
                variant="outline"
                className="w-full rounded-sm"
              >
                Clear Filters
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">
              {filteredWarehouses.length} Warehouses Found
            </h3>
            {filteredWarehouses.map((warehouse) => (
              <Card
                key={warehouse.id}
                data-testid={`warehouse-card-${warehouse.id}`}
                className="bg-white border border-border rounded-sm shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/customer/warehouse/${warehouse.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-heading text-lg font-bold text-foreground">{warehouse.name}</h4>
                  {warehouse.temperature_controlled && (
                    <Thermometer className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                  <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  <span>{warehouse.location.city}, {warehouse.location.state}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {warehouse.storage_types.map((type) => (
                    <span key={type} className="px-2.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wide">
                      {type.replace('_', ' ')}
                    </span>
                  ))}
                </div>
                {warehouse.price_per_sqm > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-sm font-medium text-foreground">
                      From <span className="font-bold text-lg">${warehouse.price_per_sqm}</span>/SQM
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-secondary/30 p-4">
          <Card className="h-full w-full rounded-sm overflow-hidden border border-border">
            <MapContainer
              center={mapCenter}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              data-testid="warehouse-map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              {filteredWarehouses.map((warehouse) => (
                <Marker
                  key={warehouse.id}
                  position={[warehouse.location.latitude, warehouse.location.longitude]}
                >
                  <Popup>
                    <div className="p-2">
                      <h4 className="font-bold text-foreground mb-1">{warehouse.name}</h4>
                      <p className="text-sm text-muted-foreground">{warehouse.location.city}</p>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/customer/warehouse/${warehouse.id}`)}
                        className="mt-2 w-full rounded-sm"
                      >
                        View Details
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerSearch;